import type { GrCanvasItem } from '../canvas/canvas.types';

interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

function clampX(x: number, colSpan: number, cols: number): number {
  return Math.min(Math.max(Math.round(x), 0), Math.max(0, cols - colSpan));
}

function boxesOverlap(a: Box, b: Box): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function collidesAny(box: Box, placed: ReadonlyArray<Box>): boolean {
  return placed.some((other) => boxesOverlap(box, other));
}

function firstFreeSlot(
  placed: ReadonlyArray<Box>,
  w: number,
  h: number,
  cols: number,
  fromY: number,
): { x: number; y: number } {
  for (let y = fromY; ; y++) {
    for (let x = 0; x <= cols - w; x++) {
      if (!collidesAny({ x, y, w, h }, placed)) return { x, y };
    }
  }
}

function hasCoords(item: GrCanvasItem): boolean {
  return Number.isFinite(item.x) && Number.isFinite(item.y);
}

function bottomOf(items: ReadonlyArray<GrCanvasItem>): number {
  return items.reduce((max, item) => Math.max(max, (item.y ?? 0) + item.rowSpan), 0);
}

function resolveCollisions<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  anchorId: string | null,
  cols: number,
): T[] {
  const order = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      if (anchorId !== null) {
        if (a.item.id === anchorId) return -1;
        if (b.item.id === anchorId) return 1;
      }
      const ay = a.item.y ?? 0;
      const by = b.item.y ?? 0;
      if (ay !== by) return ay - by;
      const ax = a.item.x ?? 0;
      const bx = b.item.x ?? 0;
      if (ax !== bx) return ax - bx;
      return a.index - b.index;
    });

  const placed: Box[] = [];
  const positioned = new Map<string, { x: number; y: number }>();
  for (const { item } of order) {
    const w = Math.min(item.colSpan, cols);
    const h = item.rowSpan;
    const desiredX = clampX(item.x ?? 0, w, cols);
    const desiredY = Math.max(0, Math.round(item.y ?? 0));

    const { x, y } = collidesAny({ x: desiredX, y: desiredY, w, h }, placed)
      ? firstFreeSlot(placed, w, h, cols, desiredY)
      : { x: desiredX, y: desiredY };

    placed.push({ x, y, w, h });
    positioned.set(item.id, { x, y });
  }

  return items.map((item) => {
    const pos = positioned.get(item.id);
    return pos ? { ...item, x: pos.x, y: pos.y } : item;
  });
}

function autoFlow<T extends GrCanvasItem>(items: ReadonlyArray<T>, cols: number): T[] {
  const placed: Box[] = [];
  const result: T[] = [];
  for (const item of items) {
    const w = Math.min(item.colSpan, cols);
    const h = item.rowSpan;
    const { x, y } = firstFreeSlot(placed, w, h, cols, 0);
    placed.push({ x, y, w, h });
    result.push({ ...item, x, y });
  }
  return result;
}

export function normalize<T extends GrCanvasItem>(items: ReadonlyArray<T>, cols: number): T[] {
  if (items.length === 0) return [];
  return items.every(hasCoords) ? resolveCollisions(items, null, cols) : autoFlow(items, cols);
}

export function moveItem<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  id: string,
  targetX: number,
  targetY: number,
  cols: number,
): T[] {
  const dragged = items.find((item) => item.id === id);
  if (!dragged) return items.slice();
  const x = clampX(targetX, dragged.colSpan, cols);
  const y = Math.max(0, Math.round(targetY));
  const next = items.map((item) => (item.id === id ? { ...item, x, y } : item));
  return resolveCollisions(next, id, cols);
}

export function resizeItem<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  id: string,
  colSpan: number,
  rowSpan: number,
  cols: number,
): T[] {
  const next = items.map((item) =>
    item.id === id
      ? { ...item, colSpan, rowSpan, x: clampX(item.x ?? 0, colSpan, cols) }
      : item,
  );
  return resolveCollisions(next, id, cols);
}

export function addItem<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  item: T,
  cols: number,
): T[] {
  const seeded = { ...item, x: 0, y: bottomOf(items) };
  return resolveCollisions([...items, seeded], seeded.id, cols);
}

export function addItemAt<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  item: T,
  x: number,
  y: number,
  cols: number,
): T[] {
  const w = Math.min(item.colSpan, cols);
  const clampedX = clampX(x, w, cols);
  const clampedY = Math.max(0, Math.round(y));
  const seeded = { ...item, x: clampedX, y: clampedY };
  return resolveCollisions([...items, seeded], seeded.id, cols);
}

export function removeItem<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  id: string,
  cols: number,
): T[] {
  return resolveCollisions(
    items.filter((item) => item.id !== id),
    null,
    cols,
  );
}

export function deleteRows<T extends GrCanvasItem>(
  items: ReadonlyArray<T>,
  startRow: number,
  count: number,
): T[] {
  const endRow = startRow + count;
  return items.map((item) => {
    const y = item.y ?? 0;
    if (y < endRow) return item;
    return { ...item, y: y - count };
  });
}
